import React, { useEffect, useRef, useState } from "react";

/**
 * Measures its container and renders children only once non-zero pixel
 * dimensions are known. Two API styles:
 *   - render-prop:  <DeferredMount>{(w, h) => <BarChart width={w} height={h}>…}</DeferredMount>
 *   - plain children (legacy): just hides until the box is sized.
 *
 * This replaces the previous wrapper-only strategy because Recharts'
 * <ResponsiveContainer> logs `width(-1) and height(-1)` on its first
 * internal render — the only reliable fix is to bypass ResponsiveContainer
 * and pass numeric width/height directly to BarChart/PieChart/LineChart.
 */
export default function DeferredMount({ children, fallback = null }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        setSize((prev) => (prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const ready = size.w > 0 && size.h > 0;
  return (
    <div ref={ref} className="w-full h-full" data-testid="deferred-mount">
      {ready ? (typeof children === "function" ? children(size.w, size.h) : children) : fallback}
    </div>
  );
}
